/* eslint-disable object-curly-newline,dot-notation,jsx-a11y/no-static-element-interactions */
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// .babelrc.js, .babel-plugin-macrosrc.js are required for below macro
import { faEyeSlash, faEye, faTimes, faCheck } from '@fortawesome/pro-solid-svg-icons';
import './TextInput.css';

// Utils
import { prettifyPhone } from '../../common/utils/prettify';

function getErrors({ value, type, label, required }) {
  const errors = { true: [], false: [] }; // returns obj where errors.false are displayed below input
  if (!value && required) {
    const requiredError = `${label} is required.`;
    errors[false].push(requiredError);
  } else {
    // universal booleans
    const containsCharacter = /[a-zA-Z]/.test(value);
    const containsNumber = /\d/.test(value);
    const containsLowercase = value.toUpperCase() !== value;
    const containsUppercase = value.toLowerCase() !== value;

    // prevent mobile users from entering emojis - only display if they entered one
    const containsEmoji = /\p{Emoji_Presentation}/ug.test(value);
    if (containsEmoji) {
      errors[!containsEmoji].push(`${label} cannot contain emojis.`);
    }

    switch (type) {
      case 'text': {
        const inputValid = !containsNumber;
        errors[inputValid].push(`${label} cannot contain numbers.`);
        break;
      }
      case 'code': {
        const inputValid = !containsCharacter;
        errors[inputValid].push(`${label} cannot contain characters.`);
        break;
      }
      case 'phone': {
        const inputValid = !containsCharacter;
        errors[inputValid].push(`${label} cannot contain characters.`);
        break;
      }
      case 'email': {
        // note(joeysapp): This doesn't allow for +flags in emails.
        /* eslint-disable no-useless-escape */
        const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/g;
        const inputValid = emailRegex.test(value);
        errors[inputValid].push('Incorrect email address.');
        break;
      }
      case 'password': {
        const lengthRequirement = value.length > 7;
        errors[lengthRequirement].push(`${label} length must be over 7.`);
        errors[containsLowercase].push(`${label} must contain lowercase characters.`);
        errors[containsUppercase].push(`${label} must contain uppercase characters.`);
        errors[containsNumber].push(`${label} must contain numeric characters.`);
        break;
      }
      case 'date': {
        let d = (new Date() - new Date(value));
        d /= (1000 * 60 * 60 * 24 * 365); // ms to year
        const yearsAllowed = 250;
        const errorMsg = `${label} cannot occur over ${yearsAllowed} years in the ${d > 0 ? 'past' : 'future'}.`;
        const inputValid = d < yearsAllowed && d > -yearsAllowed;
        if (!inputValid) errors.false.push(errorMsg);
        break;
      }
      default: break;
    }
  }
  return errors;
}

export default function TextInput({
  // required
  value = '',
  setValue = () => {},
  errors = { true: [], false: [] }, // lists of error strings
  setErrors = () => {},
  type = 'text', // text, code, password, email, phone, date
  label = '',

  // optional
  style,
  className,
  id,
  disabled = false,
  required = false,
  maxLength = 128,
  placeholder = '',
  toggleVisibility = false, // used for password
  showErrors = false, // used for password, always show reqs
  onEnter = null,
}) {
  const [ visible, toggleVisible ] = useState(false);

  function handleChange(event) {
    const newValue = event.target.value;
    if (!newValue || newValue.length <= maxLength) {
      setValue(newValue);
    }
    const newErrors = getErrors({ value: newValue, label: label || placeholder, type, required });
    if (type === 'date') {
      // do not set errors while user still in calendar modal (browser specific)
    } else if (newValue && errors.false.length) { // when user clears input, leave errors
      setErrors(newErrors); // also do not set errors on first focus
    } else if (type === 'password') { // leave all errors, set to all false
      const validErrors = errors.true;
      setErrors({ false: [...errors.false, ...validErrors], true: [] });
    }
  }

  function handleBlur() {
    if (value) {
      const newErrors = getErrors({ value, label: (label || placeholder), type, required });
      setErrors(newErrors);
    }
  }

  function handleFocus() {
    if (showErrors) { // user focuses into password creation - do not show required error
      const newErrors = getErrors({ value, label: (label || placeholder), type, required: false });
      setErrors(newErrors);
    }
  }

  function handleKeyDown(e) {
    if (onEnter && e.key === 'Enter') {
      onEnter();
    }
  }

  // specific display logic
  let inputType = type;
  const inputStyle = {};
  if (type === 'phone') {
    // match caret location and hide value, display (###) ###-####
    inputStyle.color = 'transparent';
    inputStyle.caretColor = 'black';
    inputStyle.paddingLeft = '16px'; // move caret over
    const l = value.length; // space out transparent characters so selection matches
    // needs to be tested on all devices, this works on macOS to space out cursor
    inputStyle.letterSpacing = `${(l > 0 && 2) + (l > 2 && 2) + (l > 8 && -1)}px`;
  }
  if (toggleVisibility) {
    inputStyle.paddingRight = '15%'; // space for eye
    if (type === 'password' && visible) {
      // note(joeysapp): This allows users to ctrl-c out password values
      inputType = 'text'; // make pw visible
    }
  }

  const hasErrors = (errors.false && errors.false.length) || showErrors;
  return (
    <div className={className} style={style} alt={label}>
      {
        label
          ? <label htmlFor={label} className="form-label fs-4 fw-bold">{label}</label>
          : null
      }
      {
        required
          ? <span className="text-danger fw-bold fs-2" style={{ lineHeight: '0px' }}>*</span>
          : null
      }
      <div className="d-flex position-relative">
        <input
          className={`text-input form-control ${hasErrors ? 'error border-danger' : ''}`}
          style={inputStyle}
          id={id}
          type={inputType}
          disabled={disabled}
          value={value}
          placeholder={placeholder}
          required={required}
          tabIndex={0}
          aria-label={label}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
        />
        <span
          className="position-absolute align-items-center justify-content-center h-100 fs-4"
          style={{ right: '0', cursor: 'pointer', width: '15%', display: toggleVisibility ? 'flex' : 'none' }}
          onClick={() => toggleVisible(!visible)}
        >
          {
            visible
              ? <FontAwesomeIcon icon={faEyeSlash} />
              : <FontAwesomeIcon icon={faEye} />
           }
        </span>
        {
          type === 'phone'
            ? (
              <div className="position-absolute ms-3 pe-none" style={{ lineHeight: '50px' }}>
                {prettifyPhone(value)}
              </div>
            )
            : null
        }
      </div>
      <div className={`text-input-errors fs-6 mt-1 mb-1 ${hasErrors ? 'show' : ''}`}>
        {
          [...errors.true, ...errors.false].sort().map((error) => {
            const isError = errors.false.indexOf(error) !== -1;
            // only display errors.true if props.showErrors (password)
            return (
              <div className={`d-flex align-items-center w-auto ${isError ? 'text-danger' : 'text-success'}`} key={error}>
                {
                  isError
                    ? <FontAwesomeIcon className="fs-5 me-1" style={{ minWidth: '18px' }} icon={faTimes} />
                    : <FontAwesomeIcon className="fs-5 me-1" style={{ minWidth: '18px' }} icon={faCheck} />
                }
                {error}
              </div>
            );
          })
        }
      </div>
    </div>
  );
}
